import React, { useEffect, useState } from "react";
import api from "@/lib/apiClient";
import { toast } from "sonner";
import { CaretDown, DownloadSimple } from "@phosphor-icons/react";

const STATUSES = ["pending", "processing", "shipped", "delivered", "cancelled"];

const STATUS_COLOR = {
  pending: "bg-yellow-100 text-yellow-800",
  processing: "bg-blue-100 text-blue-800",
  shipped: "bg-indigo-100 text-indigo-800",
  delivered: "bg-green-100 text-green-800",
  cancelled: "bg-red-100 text-red-700",
};

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [filter, setFilter] = useState("");

  const load = () => {
    setLoading(true);
    api.get("/admin/orders", { params: filter ? { status: filter } : {} })
      .then((r) => setOrders(r.data))
      .finally(() => setLoading(false));
  };
  useEffect(load, [filter]);

  const updateStatus = async (order, status) => {
    try {
      await api.patch(`/admin/orders/${order.id}`, { status });
      toast.success(`Order ${order.order_number} → ${status}`);
      load();
    } catch { toast.error("Update failed"); }
  };

  return (
    <div className="p-8 lg:p-12" data-testid="admin-orders">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="overline text-[color:var(--vt-orange)] mb-2">/ SALES</div>
          <h1 className="font-display font-black text-4xl tracking-tighter">Orders</h1>
          <p className="text-neutral-500 text-sm mt-1">{orders.length} order(s)</p>
        </div>
        <select className="vt-input max-w-xs" value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="orders-filter">
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <div className="bg-white border border-neutral-200">
        {loading ? <div className="p-12 text-center text-neutral-500">Loading...</div> :
         orders.length === 0 ? <div className="p-12 text-center text-neutral-500">No orders yet.</div> :
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 border-b border-neutral-200">
            <tr>
              <th className="text-left p-3 overline text-neutral-600">Order</th>
              <th className="text-left p-3 overline text-neutral-600">Customer</th>
              <th className="text-left p-3 overline text-neutral-600">Date</th>
              <th className="text-right p-3 overline text-neutral-600">Items</th>
              <th className="text-right p-3 overline text-neutral-600">Total</th>
              <th className="text-left p-3 overline text-neutral-600">Payment</th>
              <th className="text-left p-3 overline text-neutral-600">Status</th>
              <th className="p-3 overline text-neutral-600"></th>
            </tr>
          </thead>
          <tbody>
            {orders.map((o) => (
              <React.Fragment key={o.id}>
                <tr className="border-b border-neutral-100 hover:bg-neutral-50 cursor-pointer" onClick={() => setExpanded(expanded === o.id ? null : o.id)} data-testid={`order-row-${o.order_number}`}>
                  <td className="p-3 font-bold">#{o.order_number}</td>
                  <td className="p-3">
                    <div>{o.shipping?.name}</div>
                    <div className="text-[10px] text-neutral-500">{o.shipping?.city}, {o.shipping?.country}</div>
                  </td>
                  <td className="p-3 text-xs">{new Date(o.created_at).toLocaleString()}</td>
                  <td className="p-3 text-right">{o.items?.length}</td>
                  <td className="p-3 text-right font-bold">€{o.total?.toFixed(2)}</td>
                  <td className="p-3 text-xs uppercase">{o.payment_method}</td>
                  <td className="p-3">
                    <select
                      className={`text-xs px-2 py-1 font-bold uppercase ${STATUS_COLOR[o.status] || ""}`}
                      value={o.status}
                      onChange={(e) => { e.stopPropagation(); updateStatus(o, e.target.value); }}
                      onClick={(e) => e.stopPropagation()}
                      data-testid={`status-${o.order_number}`}
                    >
                      {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                  <td className="p-3"><CaretDown size={14} className={`transition-transform ${expanded === o.id ? "rotate-180" : ""}`} /></td>
                </tr>
                {expanded === o.id && (
                  <tr className="bg-neutral-50">
                    <td colSpan="8" className="p-4">
                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <div className="overline text-neutral-500 mb-2">Items</div>
                          {o.items.map((it) => (
                            <div key={it.product_id} className="flex justify-between text-xs py-1 border-b border-neutral-200">
                              <span>{it.name} × {it.quantity}</span>
                              <span className="font-bold">€{it.line_total.toFixed(2)}</span>
                            </div>
                          ))}
                        </div>
                        <div>
                          <div className="overline text-neutral-500 mb-2">Shipping</div>
                          <div className="text-xs">
                            <div className="font-bold">{o.shipping?.name}</div>
                            <div>{o.shipping?.address}</div>
                            <div>{o.shipping?.postal} {o.shipping?.city}, {o.shipping?.country}</div>
                            <div>{o.shipping?.phone}</div>
                          </div>
                          {o.notes && <><div className="overline text-neutral-500 mt-3 mb-1">Notes</div><div className="text-xs">{o.notes}</div></>}
                        </div>
                        {(o.status === "paid" || o.invoice_generated) && (
                          <div className="mt-4 pt-4 border-t border-neutral-200">
                            <a
                              href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/orders/${o.id}/invoice`}
                              target="_blank" rel="noopener noreferrer"
                              className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[color:var(--vt-orange)] link-underline"
                              data-testid={`admin-invoice-${o.order_number}`}
                            >
                              <DownloadSimple size={14} weight="bold" /> Download Invoice
                            </a>
                            {o.invoice_number && (
                              <span className="ml-3 text-xs text-neutral-500">{o.invoice_number}</span>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>}
      </div>
    </div>
  );
}
